import { useCallback, useState } from 'react';
import {
  getItemsAtLevel,
  getLevelConfigCandidates,
  updateTabItemFlags,
} from './detailTabsManagementUseCase';

/**
 * @typedef useDetailTabsManagementReturn
 * @property {(levelParentIds: string[]) => DetailTabItem[]} getAllItemsAtLevel
 * @property {(levelParentIds: string[]) => DetailTabItem[]} getLevelConfigCandidatesAtLevel
 * @property {(tabIdPath: string[], flags: Partial<{ used: boolean, available: boolean }>) => DetailTabItem[]} getTabsAfterToggle
 * @property {(tabIdPath: string[], flags: Partial<{ used: boolean, available: boolean }>) => void} setTabFlags
 * @property {() => void} startConfigDraft
 * @property {() => void} discardConfigDraft
 * @property {() => DetailTabItem[] | null} getDraftTabs
 */

/**
 * Detail tab configuration helpers.
 *
 * Add/close/apply actions persist via POST (see useDetailTabsSaveMutation).
 * Close is optimistic; add and apply wait for POST success before updating the bar.
 * keeps a local `configDraft` to accumulate `available` toggles until "Appliquer".
 * @param {object} params
 * @param {DetailTabItem[]} params.tabs - Tabs from GET
 * @returns {useDetailTabsManagementReturn}
 */
const useDetailTabsManagement = ({ tabs }) => {
  /** @type {[DetailTabItem[] | null, React.Dispatch<React.SetStateAction<DetailTabItem[] | null>>]} */
  const [configDraft, setConfigDraft] = useState(null);

  const getTabsAfterToggle = useCallback(
    (tabIdPath, flags) => updateTabItemFlags(tabs, tabIdPath, flags),
    [tabs],
  );

  const getAllItemsAtLevel = useCallback(
    (levelParentIds = []) => getItemsAtLevel(configDraft ?? tabs, levelParentIds),
    [configDraft, tabs],
  );

  const getLevelConfigCandidatesAtLevel = useCallback(
    (levelParentIds = []) => getLevelConfigCandidates(getItemsAtLevel(tabs, levelParentIds)),
    [tabs],
  );

  const startConfigDraft = useCallback(() => {
    setConfigDraft(structuredClone(tabs));
  }, [tabs]);

  const discardConfigDraft = useCallback(() => {
    setConfigDraft(null);
  }, []);

  const setTabFlags = useCallback((tabIdPath, flags) => {
    setConfigDraft((prev) => (prev ? updateTabItemFlags(prev, tabIdPath, flags) : prev));
  }, []);

  const getDraftTabs = useCallback(() => configDraft, [configDraft]);

  return {
    getAllItemsAtLevel,
    getLevelConfigCandidatesAtLevel,
    getTabsAfterToggle,
    setTabFlags,
    startConfigDraft,
    discardConfigDraft,
    getDraftTabs,
  };
};

export default useDetailTabsManagement;
